/* eslint-disable react-refresh/only-export-components */
/**
 * Shared admin login gate.
 * Validates the password server-side against ADMIN_PASSWORD (never exposed to browser).
 */
import React, { useState } from 'react';
import { Shield, Lock, Eye, EyeOff } from '../lib/icons';
import { setAdminToken } from '../lib/image-slots-client';

const SESSION_KEY = 'gfs_admin_auth';
const API_BASE    = import.meta.env.DEV ? 'http://localhost:3001' : '';

export function isAdminAuthed(): boolean {
  return sessionStorage.getItem(SESSION_KEY) === '1';
}

export function clearAdminAuth() {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem('gfs_admin_token');
}

export function AdminLoginGate({ onAuth }: { onAuth: () => void }) {
  const [pw, setPw]         = useState('');
  const [show, setShow]     = useState(false);
  const [err, setErr]       = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API_BASE}/api/cms/auth`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password: pw }),
      });
      const data = await res.json() as { ok: boolean; token?: string; error?: string };
      if (data.ok) {
        sessionStorage.setItem(SESSION_KEY, '1');
        if (data.token) setAdminToken(data.token);
        onAuth();
      } else {
        setErr(data.error ?? 'Incorrect password');
        setPw('');
      }
    } catch {
      setErr('Network error — make sure the server is running');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Shield className="h-6 w-6 text-[#FF1744]" />
          <span className="text-[#E0E0E0] font-bold text-lg">Internal Access Only</span>
        </div>
        <form onSubmit={submit} className="bg-gray-900 border border-gray-800 rounded-2xl p-8 space-y-4">
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              value={pw}
              onChange={e => setPw(e.target.value)}
              placeholder="Admin password"
              autoFocus
              disabled={loading}
              className="w-full bg-black/40 border border-gray-700 rounded-xl px-4 py-3 text-[#E0E0E0] placeholder-gray-600 pr-10 focus:outline-none focus:border-[#FF1744]/60 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={() => setShow(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FF1744] hover:bg-[#D50000] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            <Lock className="h-4 w-4 inline mr-2" />
            {loading ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  );
}
